import { httpClient } from "../../../lib/http/axios";
import type { components } from "../../../api/generated";
import type { ApiEnvelope } from "../../../lib/http/contracts";
import {
  mapCrmCollaborationTask,
  mapCrmCollaborationTaskImportConfirmResult,
  mapCrmCollaborationTaskImportResult,
  mapCrmCollaborationTaskItem,
  mapCrmCollaborationTaskItemListPage,
  mapCrmCollaborationTaskListPage,
  type CrmCollaborationTaskImportConfirmResultRaw,
  type CrmCollaborationTaskImportResultRaw,
  type CrmCollaborationTaskItemListPageRaw,
  type CrmCollaborationTaskItemRaw,
  type CrmCollaborationTaskListPageRaw,
  type CrmCollaborationTaskRaw,
} from "../api/adapters";
import type {
  CrmCollaborationTask,
  CrmCollaborationTaskCreatePayload,
  CrmCollaborationTaskImportConfirmResult,
  CrmCollaborationTaskImportPayload,
  CrmCollaborationTaskImportResult,
  CrmCollaborationTaskClaimByOwnerResult,
  CrmCollaborationTaskItem,
  CrmCollaborationTaskItemListPage,
  CrmCollaborationTaskListPage,
  CrmCollaborationTaskOriginalOwnerOption,
  CrmCollaborationTaskPublisherOption,
  CrmCollaborationStaffOption,
} from "../types";

type TaskListParams = {
  page: number;
  pageSize: number;
  taskType?: string;
  targetType?: string;
  taskStatus?: string;
  ownerUserId?: number;
  keyword?: string;
};

type CollaborationTaskMutation = components["schemas"]["CollaborationTaskMutation"];
type CollaborationTaskImportMutation = components["schemas"]["CollaborationTaskImportMutation"];
type CollaborationTaskImportConfirmMutation =
  components["schemas"]["CollaborationTaskImportConfirmMutation"];
type CollaborationTaskPublishMutation = components["schemas"]["CollaborationTaskPublishMutation"];
type CollaborationTaskCancelMutation = components["schemas"]["CollaborationTaskCancelMutation"];
type CollaborationTaskClaimMutation = components["schemas"]["CollaborationTaskClaimMutation"];
type CollaborationTaskClaimByOwnerMutation =
  components["schemas"]["CollaborationTaskClaimByOwnerMutation"];
type CollaborationTaskReleaseByOwnerMutation =
  components["schemas"]["CollaborationTaskReleaseByOwnerMutation"];
type CollaborationTaskReturnByOwnerMutation =
  components["schemas"]["CollaborationTaskReturnByOwnerMutation"];
type CollaborationTaskReleaseMutation = components["schemas"]["CollaborationTaskReleaseMutation"];
type CollaborationTaskReturnResult = components["schemas"]["CollaborationTaskReturnResult"];
type SysStaffListPayload = components["schemas"]["SysStaffListPayload"];
type SysStaffItem = components["schemas"]["SysStaffItem"];

/** 查询协作任务批次列表。 */
export async function fetchCrmCollaborationTaskList(params: TaskListParams): Promise<CrmCollaborationTaskListPage> {
  const response = await httpClient.get<ApiEnvelope<CrmCollaborationTaskListPageRaw>>("/crm/collaboration/tasks", {
    params: {
      page: params.page,
      page_size: params.pageSize,
      task_type: params.taskType,
      target_type: params.targetType,
      task_status: params.taskStatus,
      owner_user_id: params.ownerUserId,
      keyword: params.keyword,
    },
  });
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("协作任务批次接口返回了无效数据");
  }
  return mapCrmCollaborationTaskListPage(response.data.data);
}

/** 创建协作任务草稿。 */
export async function createCrmCollaborationTask(payload: CrmCollaborationTaskCreatePayload): Promise<CrmCollaborationTask> {
  const requestBody: CollaborationTaskMutation = {
    task_name: payload.taskName,
    task_type: payload.taskType,
    target_type: payload.targetType,
    assignment_mode: payload.assignmentMode,
    owner_user_id: payload.ownerUserId,
    default_assigned_user_id: payload.defaultAssignedUserId,
    source_type: payload.sourceType,
    remark: payload.remark,
  };
  const response = await httpClient.post<ApiEnvelope<CrmCollaborationTaskRaw>>("/crm/collaboration/tasks", requestBody);
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("创建协作任务接口返回了无效数据");
  }
  return mapCrmCollaborationTask(response.data.data);
}

/** 导入并校验任务对象 ID。 */
export async function createCrmCollaborationTaskImport(
  taskId: number,
  payload: CrmCollaborationTaskImportPayload,
): Promise<CrmCollaborationTaskImportResult> {
  const requestBody: CollaborationTaskImportMutation = {
    import_type: payload.importType,
    raw_text: payload.rawText,
    created_by: payload.createdBy,
    remark: payload.remark,
  };
  const response = await httpClient.post<ApiEnvelope<CrmCollaborationTaskImportResultRaw>>(
    `/crm/collaboration/tasks/${taskId}/imports`,
    requestBody,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("协作任务导入校验接口返回了无效数据");
  }
  return mapCrmCollaborationTaskImportResult(response.data.data);
}

/** 确认导入任务对象。 */
export async function confirmCrmCollaborationTaskImport(
  taskId: number,
  importId: number,
  currentUserId: number,
): Promise<CrmCollaborationTaskImportConfirmResult> {
  const requestBody: CollaborationTaskImportConfirmMutation = { current_user_id: currentUserId };
  const response = await httpClient.post<ApiEnvelope<CrmCollaborationTaskImportConfirmResultRaw>>(
    `/crm/collaboration/tasks/${taskId}/imports/${importId}/confirm`,
    requestBody,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("确认导入接口返回了无效数据");
  }
  return mapCrmCollaborationTaskImportConfirmResult(response.data.data);
}

/** 发布协作任务。 */
export async function publishCrmCollaborationTask(taskId: number, currentUserId: number): Promise<CrmCollaborationTask> {
  const requestBody: CollaborationTaskPublishMutation = { current_user_id: currentUserId };
  const response = await httpClient.post<ApiEnvelope<CrmCollaborationTaskRaw>>(
    `/crm/collaboration/tasks/${taskId}/publish`,
    requestBody,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("发布协作任务接口返回了无效数据");
  }
  return mapCrmCollaborationTask(response.data.data);
}

/** 取消协作任务。 */
export async function cancelCrmCollaborationTask(taskId: number, currentUserId: number): Promise<CrmCollaborationTask> {
  const requestBody: CollaborationTaskCancelMutation = { current_user_id: currentUserId };
  const response = await httpClient.post<ApiEnvelope<CrmCollaborationTaskRaw>>(
    `/crm/collaboration/tasks/${taskId}/cancel`,
    requestBody,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("取消协作任务接口返回了无效数据");
  }
  return mapCrmCollaborationTask(response.data.data);
}

/** 查询电销或强跟任务池。 */
export async function fetchCrmCollaborationPoolItems(params: {
  pool: "tele-sales" | "strong-follow";
  currentUserId: number;
  page: number;
  pageSize: number;
  viewScope?: "all" | "mine";
  keyword?: string;
  province?: string;
  orgName?: string;
  originalOwnerUserIds?: number[];
}): Promise<CrmCollaborationTaskItemListPage> {
  const response = await httpClient.get<ApiEnvelope<CrmCollaborationTaskItemListPageRaw>>(
    `/crm/collaboration/pools/${params.pool}`,
    {
      params: {
        current_user_id: params.currentUserId,
        page: params.page,
        page_size: params.pageSize,
        view_scope: params.viewScope ?? "all",
        ...(params.keyword?.trim() ? { keyword: params.keyword.trim() } : {}),
        ...(params.province?.trim() ? { province: params.province.trim() } : {}),
        ...(params.orgName?.trim() ? { org_name: params.orgName.trim() } : {}),
        ...(params.originalOwnerUserIds?.length
          ? { original_owner_user_ids: params.originalOwnerUserIds }
          : {}),
      },
      // FastAPI 多值查询需要重复键；避免默认 `[]` / `[0]` 后缀导致筛选失效。
      paramsSerializer: { indexes: null },
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("协作任务池接口返回了无效数据");
  }
  return mapCrmCollaborationTaskItemListPage(response.data.data);
}

/** 查询任务池全部可认领发布人，候选不受表格当前分页限制。 */
export async function fetchCrmCollaborationPoolPublishers(
  poolCode: "tele_sales_pool" | "strong_follow_pool",
): Promise<CrmCollaborationTaskPublisherOption[]> {
  const response = await httpClient.get<ApiEnvelope<Array<{
    owner_user_id: number;
    owner_user_name?: string | null;
    available_item_count: number;
  }>>>("/crm/collaboration/pools/publishers", {
    params: { pool_code: poolCode },
  });
  if (response.data.code !== "OK" || !Array.isArray(response.data.data)) {
    throw new Error("任务池发布人候选接口返回了无效数据");
  }
  return response.data.data.map((item) => ({
    ownerUserId: item.owner_user_id,
    ownerUserName: item.owner_user_name?.trim() || `用户 ${item.owner_user_id}`,
    itemCount: item.available_item_count,
  }));
}

/** 查询任务池全部可认领对象的原负责人，候选不受表格当前分页限制。 */
export async function fetchCrmCollaborationPoolOriginalOwners(
  poolCode: "tele_sales_pool" | "strong_follow_pool",
): Promise<CrmCollaborationTaskOriginalOwnerOption[]> {
  const response = await httpClient.get<ApiEnvelope<Array<{
    original_owner_user_id: number;
    original_owner_user_name?: string | null;
    available_item_count: number;
  }>>>("/crm/collaboration/pools/original-owners", {
    params: { pool_code: poolCode },
  });
  if (response.data.code !== "OK" || !Array.isArray(response.data.data)) {
    throw new Error("任务池原负责人候选接口返回了无效数据");
  }
  return response.data.data.map((item) => ({
    originalOwnerUserId: item.original_owner_user_id,
    originalOwnerUserName:
      item.original_owner_user_name?.trim() || `用户 ${item.original_owner_user_id}`,
    itemCount: item.available_item_count,
  }));
}

/** 查询我的协作任务对象。 */
export async function fetchMyCrmCollaborationTaskItems(params: {
  currentUserId: number;
  page: number;
  pageSize: number;
}): Promise<CrmCollaborationTaskItemListPage> {
  const response = await httpClient.get<ApiEnvelope<CrmCollaborationTaskItemListPageRaw>>("/crm/collaboration/my-items", {
    params: {
      current_user_id: params.currentUserId,
      page: params.page,
      page_size: params.pageSize,
    },
  });
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("我的协作任务接口返回了无效数据");
  }
  return mapCrmCollaborationTaskItemListPage(response.data.data);
}

/** 查询当前用户全部已认领任务对应的发布人。 */
export async function fetchMyCrmCollaborationTaskPublishers(): Promise<CrmCollaborationTaskPublisherOption[]> {
  const response = await httpClient.get<ApiEnvelope<Array<{
    owner_user_id: number;
    owner_user_name?: string | null;
    claimed_item_count: number;
  }>>>("/crm/collaboration/my-items/publishers");
  if (response.data.code !== "OK" || !Array.isArray(response.data.data)) {
    throw new Error("我的协作任务发布人候选接口返回了无效数据");
  }
  return response.data.data.map((item) => ({
    ownerUserId: item.owner_user_id,
    ownerUserName: item.owner_user_name?.trim() || `用户 ${item.owner_user_id}`,
    itemCount: item.claimed_item_count,
  }));
}

/** 认领任务对象。 */
export async function claimCrmCollaborationTaskItem(itemId: number, currentUserId: number): Promise<CrmCollaborationTaskItem> {
  const requestBody: CollaborationTaskClaimMutation = { current_user_id: currentUserId };
  const response = await httpClient.post<ApiEnvelope<CrmCollaborationTaskItemRaw>>(
    `/crm/collaboration/items/${itemId}/claim`,
    requestBody,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("认领任务对象接口返回了无效数据");
  }
  return mapCrmCollaborationTaskItem(response.data.data);
}

/** 按任务发布人批量认领当前任务池中的可认领对象。 */
export async function claimCrmCollaborationTaskItemsByOwner(
  poolCode: "tele_sales_pool" | "strong_follow_pool",
  ownerUserId: number,
  currentUserId: number,
): Promise<CrmCollaborationTaskClaimByOwnerResult> {
  const requestBody: CollaborationTaskClaimByOwnerMutation = {
    pool_code: poolCode,
    owner_user_id: ownerUserId,
    current_user_id: currentUserId,
  };
  const response = await httpClient.post<ApiEnvelope<{ claimed_count: number }>>(
    "/crm/collaboration/items/claim-by-owner",
    requestBody,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("按发布人批量认领接口返回了无效数据");
  }
  return { claimedCount: response.data.data.claimed_count };
}

/** 批量释放当前用户已认领的任务对象。 */
export async function releaseCrmCollaborationTaskItems(
  taskId: number,
  itemIds: number[],
  currentUserId: number,
): Promise<{ releasedCount: number }> {
  const requestBody: CollaborationTaskReleaseMutation = {
    task_id: taskId,
    item_ids: itemIds,
    current_user_id: currentUserId,
  };
  const response = await httpClient.post<ApiEnvelope<{ released_count: number }>>(
    "/crm/collaboration/items/release",
    requestBody,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("释放协作任务对象接口返回了无效数据");
  }
  return { releasedCount: response.data.data.released_count };
}

/** 按任务发布人释放当前用户全部仍为已认领状态的任务对象。 */
export async function releaseCrmCollaborationTaskItemsByOwner(
  ownerUserId: number,
  currentUserId: number,
): Promise<{ releasedCount: number }> {
  const requestBody: CollaborationTaskReleaseByOwnerMutation = {
    owner_user_id: ownerUserId,
    current_user_id: currentUserId,
  };
  const response = await httpClient.post<ApiEnvelope<{ released_count: number }>>(
    "/crm/collaboration/items/release-by-owner",
    requestBody,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("按发布人批量释放接口返回了无效数据");
  }
  return { releasedCount: response.data.data.released_count };
}

/** 按任务发布人退回当前用户认领的公共池任务对象。 */
export async function returnCrmCollaborationTaskItemsToPoolByOwner(
  ownerUserId: number,
  currentUserId: number,
): Promise<{ returnedCount: number }> {
  const requestBody: CollaborationTaskReturnByOwnerMutation = {
    owner_user_id: ownerUserId,
    current_user_id: currentUserId,
  };
  const response = await httpClient.post<ApiEnvelope<CollaborationTaskReturnResult>>(
    "/crm/collaboration/items/return-to-pool-by-owner",
    requestBody,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("按发布人批量退回任务池接口返回了无效数据");
  }
  return { returnedCount: response.data.data.returned_count };
}

/** 把本人认领的公共池任务对象退回任务池。 */
export async function returnCrmCollaborationTaskItemToPool(
  itemId: number,
): Promise<{ returnedCount: number }> {
  const response = await httpClient.post<ApiEnvelope<CollaborationTaskReturnResult>>(
    `/crm/collaboration/items/${itemId}/return-to-pool`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("退回任务池接口返回了无效数据");
  }
  return { returnedCount: response.data.data.returned_count };
}

/** 查询可作为任务发起人或处理人的员工候选。 */
export async function fetchCrmCollaborationStaffOptions(): Promise<CrmCollaborationStaffOption[]> {
  const response = await httpClient.get<ApiEnvelope<SysStaffListPayload>>("/sys/dept-users/staff", {
    params: {
      is_staff: true,
      page: 1,
      page_size: 100,
      sort_field: "update_time",
      sort_direction: "desc",
    },
  });
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("员工候选接口返回了无效数据");
  }
  return (response.data.data.items ?? []).map(mapStaffOption);
}

/** 映射员工接口响应为协作任务员工选择项。 */
function mapStaffOption(raw: SysStaffItem): CrmCollaborationStaffOption {
  return {
    id: raw.id,
    username: raw.username,
    displayName: raw.realname || raw.nickname || raw.username || `员工 ${raw.id}`,
    mainDeptName: raw.main_dept_name ?? "",
    disabled: raw.status !== 1,
  };
}
